/**
 * Records what was done, so adoption can be reported on.
 *
 * It records the action and nothing else — no filenames, no page content, no
 * document identifiers. The server will not store anything more than this even
 * if it were sent, and there is no field here to put it in.
 *
 * Events queue in IndexedDB and flush when the server is reachable, because the
 * app is installable and works with no network: a day of offline work would
 * otherwise be invisible in the report.
 */

export type UsageAction =
  | 'import_pdf'
  | 'import_office'
  | 'import_image'
  | 'export_pdf'
  | 'convert_office'
  | 'delete_page'
  | 'rotate_page'
  | 'reorder_page'
  | 'add_blank_page'
  | 'split'
  | 'add_text'
  | 'stamp';

interface QueuedEvent {
  id?: number;
  action: UsageAction;
  quantity: number;
  at: string;
}

/*
 * Its own database rather than a store inside 'pdf-toolkit'. Adding a store
 * there would mean bumping that database's version, and a half-updated tab
 * still holding the old version open would fail to open it at all.
 */
const DB_NAME = 'pdf-toolkit-usage';
const DB_VERSION = 1;
const STORE = 'queue';
/** Enough to hold a long offline stretch; past this the oldest are dropped. */
const MAX_QUEUED = 500;

let flushing = false;

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      // Private mode or blocked storage: nothing is recorded, nothing breaks.
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
    } catch {
      resolve(null);
    }
  });
}

/**
 * Notes that something happened.
 *
 * Never awaited by callers and never throws: recording is not what the person
 * asked the app to do, so it must not be able to interrupt it.
 */
export function track(action: UsageAction, quantity = 1): void {
  if (quantity <= 0) return;
  const event: QueuedEvent = {
    action,
    quantity: Math.min(10_000, Math.round(quantity)),
    at: new Date().toISOString(),
  };
  void (async () => {
    try {
      await withStore('readwrite', (store) => store.add(event));
      await trim();
      await flush();
    } catch {
      // Recording is best-effort by design.
    }
  })();
}

/** Keeps the queue from growing without bound while offline. */
async function trim(): Promise<void> {
  const all = await withStore<QueuedEvent[]>('readonly', (store) => store.getAll());
  if (!all || all.length <= MAX_QUEUED) return;
  const excess = all.slice(0, all.length - MAX_QUEUED);
  for (const event of excess) {
    if (event.id !== undefined) {
      await withStore('readwrite', (store) => store.delete(event.id!));
    }
  }
}

/**
 * Sends everything queued, then clears exactly what was sent.
 *
 * Rows are deleted by the ids that went up rather than by clearing the store,
 * so an event recorded while the request was in flight is not lost.
 */
export async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const queued = await withStore<QueuedEvent[]>('readonly', (store) => store.getAll());
    if (!queued || queued.length === 0) return;

    const response = await fetch('/api/usage/events', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: queued.map(({ action, quantity, at }) => ({ action, quantity, at })),
      }),
    });
    // A 401 means the session lapsed; the events stay queued for the next
    // sign-in rather than being thrown away.
    if (!response.ok) return;

    for (const event of queued) {
      if (event.id !== undefined) {
        await withStore('readwrite', (store) => store.delete(event.id!));
      }
    }
  } catch {
    // Offline, or the server refused. The queue keeps them for next time.
  } finally {
    flushing = false;
  }
}

/** Flushes when the network comes back and when the tab is being left. */
export function startUsageFlushing(): () => void {
  const onOnline = () => void flush();
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void flush();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onHidden);
  void flush();
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onHidden);
  };
}
